import * as React from 'react';
import * as E from 'fp-ts/Either';
import { pipe } from 'fp-ts/lib/function';
import {
  FullMatchFragment,
  useCloseMatchVotingMutation,
} from '../../../generated/graphql';
import { useAlreadyCastVotes } from '../../Votes/useAlreadyCastVotes';
import { Card } from '../../../components/Card';
import { paths } from '../../../paths';
import { Button, ButtonLink } from '../../../components/Button';
import { LoadingSpinner } from '../../../components/LoadingContainer';
import { TextButton, TextLink } from '../../../components/ClickableText';
import { useAlert } from '../../../contexts/AlertContext';
import { buildMutationMessage } from '../../../utils/buildMutationMessage';
import { useConfirm } from '../../../contexts/ConfirmContext';
import { voteCountText } from '../../Votes/voteCountText';
import { isNil } from '../../../utils/isNil';

interface Props {
  match: FullMatchFragment;
}

const confirmProps = {
  title: 'Release votes',
  children: (
    <p>
      This will end the voting for this match and allow all the cast votes to be
      viewed. This can't be undone. Are you sure?
    </p>
  ),
};

export const CastVotesCard: React.FC<Props> = ({ match }) => {
  const { alreadyCastVotes } = useAlreadyCastVotes(match.id);

  const alert = useAlert();
  const confirm = useConfirm();

  const [closeVotesResponse, closeMatchVoting] = useCloseMatchVotingMutation();
  const submitCloseMatchVoting = async () => {
    const result = await confirm(confirmProps);

    if (result) {
      closeMatchVoting({ id: match.id }).then((res) => {
        pipe(
          buildMutationMessage('Votes released!')(
            res.data?.closeMatchVoting,
            res.error,
          ),
          E.fold((error) => alert(error, 'danger'), alert),
        );
      });
    }
  };

  const availableVoteTypes = match.team.voteTypes.filter((vt) => vt.enabled);

  const userHasVoted = availableVoteTypes.length <= alreadyCastVotes.length;

  return userHasVoted ? (
    <Card className="space-y-4 bg-white text-center">
      {match.team.isCaptain ? (
        <>
          <p>
            You've cast all your votes. You can release the votes for all to
            see.
          </p>
          <p>{voteCountText(match.voteCount, availableVoteTypes.length)}</p>
          <div>
            {closeVotesResponse.fetching ? (
              <LoadingSpinner className="mx-auto" />
            ) : (
              <div className="max-w-sm mx-auto mt-6">
                <Button
                  type="button"
                  kind="primary"
                  onClick={submitCloseMatchVoting}
                >
                  Release votes now
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <p>
            You've cast all your votes. Hang tight! They'll be available soon.
          </p>
          {isNil(match.team.player) ? (
            <p>
              <TextLink to={paths.joinTeam.team(match.team.id)}>
                Join {match.team.name}
              </TextLink>{' '}
              to read them and see the results.
            </p>
          ) : null}
        </>
      )}
    </Card>
  ) : (
    <Card className="bg-white ">
      <div className="text-center space-y-4">
        <p className="text-3xl">🗳</p>
        <p>Voting is open.</p>
        <ButtonLink to={paths.match.votes.vote(match.id)}>
          Cast your votes now!
        </ButtonLink>
        {match.team.isCaptain ? (
          closeVotesResponse.fetching ? (
            <LoadingSpinner className="mx-auto" />
          ) : (
            <p>
              You can also{' '}
              <TextButton onClick={submitCloseMatchVoting}>
                release the votes
              </TextButton>
              .
            </p>
          )
        ) : null}
      </div>
    </Card>
  );
};
